import { useCallback, useEffect, useRef, useState } from "react";
import { FetchHandler, UseFetchOptions } from "./use-fetch-type";

const useFetch = <T, P>(
  fetchHandler: FetchHandler<T, P>,
  param: P = { limit: 10, page: 0 } as P,
  options: UseFetchOptions = { autoFetch: true, infiniteScroll: false }
) => {
  const { autoFetch = true, infiniteScroll } = options;
  const currentParam = useRef(param);
  const [data, setData] = useState<T | T[] | null>([]);
  const [loading, setLoading] = useState<boolean>(autoFetch);
  const [error, setError] = useState<Error | null>(null);
  const [endOfData, setEndOfData] = useState<boolean>(false);

  useEffect(() => {
    currentParam.current = param;
  }, [param]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  const savedData = useCallback((res: T | T[]) => {
    if (Array.isArray(res)) {
      if (infiniteScroll) {
        if (res?.length === 0) {
          setEndOfData(true);
          return;
        }
        setData((prevData) => [...(prevData as T[]), ...res]);
      } else {
        setData(res);
      }
    } else {
      setData(res);
    }
  }, [infiniteScroll]);

  const startFetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchHandler(currentParam.current);
      savedData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An unknown error occurred'));
    } finally {
      setLoading(false);
    }
  }, [fetchHandler, savedData]);

  const init = useRef(true);
  useEffect(() => {
    if (init.current) {
      if(autoFetch) {
        startFetch();
      } else {
        setLoading(false);
      }
      init.current = false;
    }
  }, [startFetch, autoFetch]);

  return { 
    data: data as T, 
    loading, 
    error, 
    startFetch, 
    reset, 
    endOfData 
  };
};

export default useFetch;